import React, { FC, useState } from 'react';
import { Field, Form, Formik, FieldProps, FormikHelpers } from 'formik';
import { PatternFormat } from 'react-number-format';

import { LoginFormData } from 'types';
import { composeValidators, required, pattern, phoneRegexp, passwordRegex } from 'utils/validate';
import { ReactComponent as Eye } from 'icons/eye.svg';
import { ReactComponent as EyeOff } from 'icons/eye-off.svg';
import styles from './LoginForm.module.scss';

interface Props {
  onSubmit: (values: LoginFormData) => Promise<any>;
}

export const LoginForm: FC<Props> = ({ onSubmit }) => {
  const [showPassword, setShowPassword] = useState(false);
  const handleSubmit = async (values: LoginFormData, { setErrors }: FormikHelpers<LoginFormData>) => {
    try {
      await onSubmit(values);
    } catch (errors: any) {
      setErrors(errors);
    }
  };

  return (
    <Formik
      initialValues={{ phone: '', password: '', rememberMe: true }}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ isValid, isSubmitting }) => (
        <Form className={styles.form} noValidate>
          <Field name="phone" validate={composeValidators([required(), pattern(phoneRegexp)])}>
            {({ field, meta, form }: FieldProps) => {
              const error = meta.touched && meta.error;
              return (
                <div className={styles.field}>
                  <label htmlFor="login-phone">Телефон<span className={styles.required}>*</span></label>
                  <PatternFormat
                    id="login-phone"
                    name={field.name}
                    value={field.value}
                    onBlur={field.onBlur}
                    onValueChange={({ value }) => form.setFieldValue(field.name, value)}
                    valueIsNumericString
                    format="+7 (###) ### ## ##"
                    allowEmptyFormatting
                    type="tel"
                    autoComplete="username"
                    autoFocus
                    required
                    className={styles.input}
                    aria-invalid={!!error}
                    aria-describedby="login-phone-error"
                  />
                  <div id="login-phone-error" className={styles.error} aria-live="polite">{error || ' '}</div>
                </div>
              );
            }}
          </Field>
          <Field name="password" validate={composeValidators([required(), pattern(passwordRegex, 'Невалидный пароль')])}>
            {({ field, meta }: FieldProps) => {
              const error = meta.touched && meta.error;
              return (
                <div className={styles.field}>
                  <label htmlFor="login-password">Пароль<span className={styles.required}>*</span></label>
                  <div className={styles.passwordField}>
                    <input
                      {...field}
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Введите пароль"
                      required
                      className={styles.input}
                      aria-invalid={!!error}
                      aria-describedby="login-password-error"
                    />
                    <button
                      type="button"
                      className={styles.togglePassword}
                      onClick={() => setShowPassword(value => !value)}
                      aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? <Eye aria-hidden="true" /> : <EyeOff aria-hidden="true" />}
                    </button>
                  </div>
                  <div id="login-password-error" className={styles.error} aria-live="polite">{error || ' '}</div>
                </div>
              );
            }}
          </Field>
          <label className={styles.rememberMe}>
            <Field name="rememberMe" type="checkbox" />
            <span>Запомнить меня</span>
          </label>
          <button className={styles.submitBtn} type="submit" disabled={!isValid || isSubmitting} aria-busy={isSubmitting}>
            {isSubmitting ? 'Входим…' : 'Войти'}
          </button>
        </Form>
      )}
    </Formik>
  );
};
